import { z } from 'zod'
import validator from 'validator'

const isValidName = (name: string) => {
	const regex = /^[a-zA-Z\s]+$/
	return regex.test(name.trim())
}

const isCompanyValidName = (name: string) => {
	const regex = /^[a-zA-Z\s_-]+$/
	return regex.test(name.trim())
}


const isValidAddressLine = (address: string) => {
	const regex = /^[a-zA-Z0-9\s,.-]{3,}$/
	return regex.test(address.trim())
}

export const isZipCodeValid = (zip: string, countryName?: string): boolean => {
	if (!zip) return false
	const zipStr = zip.trim()

	const countryCodeMap: Record<string, string> = {
		'United States': 'US',
		'United States of America': 'US',
		'United Kingdom': 'GB',
		'United Kingdom of Great Britain and Northern Ireland': 'GB',
		Canada: 'CA',
		Australia: 'AU',
		Germany: 'DE',
		France: 'FR',
		Italy: 'IT',
		Spain: 'ES',
		Netherlands: 'NL',
		Belgium: 'BE',
		Switzerland: 'CH',
		Austria: 'AT',
		Sweden: 'SE',
		Norway: 'NO',
		Denmark: 'DK',
		Finland: 'FI',
		Poland: 'PL',
		Portugal: 'PT',
		Greece: 'GR',
		Ireland: 'IE',
		'New Zealand': 'NZ',
		Japan: 'JP',
		'South Korea': 'KR',
		India: 'IN',
		Brazil: 'BR',
		Mexico: 'MX',
		Argentina: 'AR',
		Chile: 'CL',
		Colombia: 'CO',
		'South Africa': 'ZA',
	}

	if (countryName && countryCodeMap[countryName]) {
		return validator.isPostalCode(zipStr, countryCodeMap[countryName] as any)
	}

	const commonFormats = [
		'US',
		'GB',
		'CA',
		'AU',
		'DE',
		'FR',
		'IT',
		'ES',
		'NL',
		'BE',
		'CH',
		'AT',
		'SE',
		'NO',
		'DK',
		'FI',
		'PL',
		'PT',
		'GR',
		'IE',
		'NZ',
	]
	return commonFormats.some((code) =>
		validator.isPostalCode(zipStr, code as any)
	)
}

export const contactFormSchema = z
	.object({
		fullName: z
			.string()
			.min(3, 'Full Name is required (min 3 chars, must be text)')
			.refine((val) => isValidName(val), {
				message: 'Full Name must contain only letters and spaces',
			}),
		preferedName: z
			.string()
			.min(3, 'Preferred Name is required (min 3 chars, must be text)')
			.refine((val) => isValidName(val), {
				message: 'Preferred Name must contain only letters and spaces',
			}),
		company: z
			.string()
			.refine(
				(val) => !val || (val.length >= 2 && isCompanyValidName(val)),
				'Company must be text (min 2 chars)'
			)
			.optional(),
		title: z
			.string()
			.refine(
				(val) => !val || (val.length >= 2 && isValidName(val)),
				'Title must be text (min 2 chars)'
			)
			.optional(),
		emails: z
			.array(
				z.object({
					type: z.number(),
					value: z
						.string()
						.min(1, 'Email is required')
						.email('Invalid email')
						.regex(
							/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
							'Invalid email'
						),
				})
			)
			.min(1, 'At least one email is required'),
		phones: z
			.array(
				z.object({
					type: z.number(),
					value: z.string(),
				})
			)
			.optional()
			.refine(
				(phones) => {
					if (!phones || phones.length === 0) return true
					const seen = new Set<string>()
					for (const phone of phones) {
						if (!phone.value || phone.value.trim() === '') continue
						const digits = phone.value.replace(/\D/g, '')
						if (digits.length < 7) return false
						if (seen.has(digits)) return false
						seen.add(digits)
					}
					return true
				},
				{ message: 'Fix phone errors before saving' }
			),
		addressLine1: z
			.string()
			.refine(
				(val) => !val || (val.length >= 3 && isValidAddressLine(val)),
				'Address Line 1 must be valid (min 3 chars)'
			)
			.optional(),
		addressLine2: z
			.string()
			.refine(
				(val) => !val || (val.length >= 3 && isValidAddressLine(val)),
				'Address Line 2 must be text (min 3 chars)'
			)
			.optional(),
		country: z.string().optional(),
		state: z
			.string()
			.refine(
				(val) => !val || (val.length >= 2 && isValidName(val)),
				'State must be text (min 2 chars)'
			)
			.optional(),
		city: z
			.string()
			.refine(
				(val) => !val || (val.length >= 2 && isValidName(val)),
				'City must contain only letters and spaces (min 2 chars)'
			)
			.optional(),
		zipCode: z.string().optional(),
	})
	.refine(
		(data) => {
			if (!data.zipCode || !data.country) return true
			return isZipCodeValid(data.zipCode, data.country)
		},
		{
			message: 'ZIP Code is not valid for the selected country',
			path: ['zipCode'],
		}
	)

export type ContactFormData = z.infer<typeof contactFormSchema>
